const RIPPLE_DURATION = 1110;
const RIPPLE_MAX_PX = 32;
const RIPPLE_BAND = 0.45;

const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
const easeOut = (t) => 1 - (1 - t) * (1 - t);

const buildLayers = (img, W, H, S, ox, oy) => {
    if (!img.naturalWidth || !img.naturalHeight) {
        return [];
    }

    const ir = img.naturalWidth / img.naturalHeight;
    let dw = 0;
    let dh = 0;

    if (ir > W / H) {
        dh = H;
        dw = H * ir;
    } else {
        dw = W;
        dh = W / ir;
    }

    const dx = (W - dw) / 2;
    const dy = (H - dh) / 2;

    const sizes = [];
    for (let s = S; Number.isInteger(s) && s >= 1; s /= 2) {
        sizes.push(s);
    }
    if (sizes[sizes.length - 1] !== 1) {
        sizes.push(1);
    }

    return sizes.reverse().map((s) => {
        const layer = document.createElement('canvas');
        layer.width = W;
        layer.height = H;
        const lctx = layer.getContext('2d');
        if (!lctx) {
            return layer;
        }

        if (s === 1) {
            lctx.drawImage(img, dx, dy, dw, dh);
            return layer;
        }

        const off = document.createElement('canvas');
        off.width = Math.ceil((W - ox) / s);
        off.height = Math.ceil((H - oy) / s);
        const octx = off.getContext('2d');
        if (!octx) {
            return layer;
        }

        octx.imageSmoothingQuality = 'medium';
        octx.setTransform(1 / s, 0, 0, 1 / s, -ox / s, -oy / s);
        octx.drawImage(img, dx, dy, dw, dh);

        lctx.imageSmoothingEnabled = false;
        lctx.drawImage(off, ox, oy, off.width * s, off.height * s);
        return layer;
    });
};

const setupItemPixelate = (item) => {
    const wrap = item.querySelector('.f-teaser-list-item__media');
    const img = wrap?.querySelector('.f-teaser-list-item__image');

    if (!wrap || !img) {
        return;
    }

    const canvas = document.createElement('canvas');
    canvas.className = 'f-teaser-list-item__pixel-canvas';
    wrap.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return;
    }

    let raf = 0;
    let prep = null;

    const prepare = () => {
        if (!img.complete || !img.naturalWidth) {
            return;
        }

        const dpr = 1;
        const W = Math.round(wrap.clientWidth * dpr);
        const H = Math.round(wrap.clientHeight * dpr);
        if (!W || !H) {
            return;
        }

        const S = Math.round(RIPPLE_MAX_PX * dpr);
        const cx = W / 2;
        const cy = H / 2;
        const ox = (Math.round(cx) % S) - S;
        const oy = (Math.round(cy) % S) - S;

        try {
            const layers = buildLayers(img, W, H, S, ox, oy);
            const rMax = Math.hypot(cx, cy);
            const cells = [];
            for (let y = oy; y < H; y += S) {
                for (let x = ox; x < W; x += S) {
                    cells.push([x, y, Math.hypot(x + S / 2 - cx, y + S / 2 - cy)]);
                }
            }

            canvas.width = W;
            canvas.height = H;
            prep = {
                S,
                layers,
                top: layers.length - 1,
                rMax,
                band: rMax * RIPPLE_BAND,
                cells,
            };
        } catch {
            prep = null;
        }
    };

    if (img.complete) {
        prepare();
    } else {
        img.addEventListener('load', prepare);
    }

    let resizeTimer = 0;
    const debouncedPrepare = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(prepare, 150);
    };
    window.addEventListener('resize', debouncedPrepare, { passive: true });

    const pulse = () => {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduceMotion) {
            return;
        }

        if (!prep) {
            prepare();
        }
        if (!prep) {
            return;
        }

        cancelAnimationFrame(raf);
        const { S, layers, top, rMax, band, cells } = prep;
        const start = performance.now();

        const draw = (now) => {
            const t = Math.min(1, Math.max(0, (now - start) / RIPPLE_DURATION));
            const R = (rMax + band) * easeOut(t);

            ctx.imageSmoothingEnabled = false;
            ctx.drawImage(layers[0], 0, 0);

            for (const [x, y, d] of cells) {
                const k = Math.max(0, 1 - Math.abs(d - R) / band);
                const lvl = Math.round(easeInOut(k) * top);
                if (lvl) {
                    ctx.drawImage(layers[lvl], x, y, S, S, x, y, S, S);
                }
            }

            if (t < 1) {
                raf = requestAnimationFrame(draw);
            } else {
                wrap.classList.remove('is-pixelating');
            }
        };

        draw(start);
        wrap.classList.add('is-pixelating');
    };

    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    const enter = () => {
        if (!canHover) {
            return;
        }
        pulse();
        item.classList.add('is-zoom');
    };

    const leave = () => {
        if (!canHover) {
            return;
        }
        item.classList.remove('is-zoom');
    };

    if (canHover) {
        item.addEventListener('mouseenter', enter);
        item.addEventListener('mouseleave', leave);
        item.addEventListener('focusin', enter);
        item.addEventListener('focusout', leave);
    }
};

const FRAME_W = 1;

const setupItemFrame = (item) => {
    const content = item.querySelector('.f-teaser-list-item__content');
    if (!content) {
        return;
    }

    let svg = content.querySelector('.f-teaser-list-item__frame');
    if (!svg) {
        content.insertAdjacentHTML(
            'afterbegin',
            '<svg class="f-teaser-list-item__frame" aria-hidden="true"><path pathLength="1"/><path pathLength="1"/><path pathLength="1"/><path pathLength="1"/></svg>',
        );
        svg = content.querySelector('.f-teaser-list-item__frame');
    }

    if (!svg) {
        return;
    }

    const updatePaths = () => {
        const w = content.offsetWidth;
        const h = content.offsetHeight;
        if (!w || !h) {
            return;
        }

        // Läser det faktiska, redan upplösta border-radius-värdet (getComputedStyle
        // löser alltid clamp()/calc() till px för en riktig CSS-egenskap, till
        // skillnad från en egen custom property) så ramen alltid matchar exakt
        // det som faktiskt renderas, oavsett avfasning eller rundning.
        const style = getComputedStyle(content);
        const trRadiusRaw = parseFloat(style.borderTopRightRadius) || 0;
        const blRadiusRaw = parseFloat(style.borderBottomLeftRadius) || 0;

        // Halva streckbredden håller ramen innanför SVG-ytan (annars klipper SVG:s
        // default overflow:hidden bort den yttre halvan av strecket på de raka
        // kanterna, medan bågarna — som redan ligger indragna — förblir fullbreda).
        // radie - i bevarar CSS-hörnens centrum oavsett indragningens storlek,
        // så det här gäller lika för rundade och avfasade hörn.
        const i = FRAME_W / 2 + (parseFloat(style.getPropertyValue('--frame-inset')) || 0);
        const bevel = parseFloat(style.getPropertyValue('--frame-bevel')) || 28;
        const straightCorner = bevel ? bevel - i * 0.586 : 0;
        const trRadius = Math.max(trRadiusRaw - i, 0);
        const blRadius = Math.max(blRadiusRaw - i, 0);

        svg.setAttribute('viewBox', `0 0 ${w} ${h}`);

        const k = (r) => r * (1 - Math.SQRT1_2);
        const tr = trRadius
            ? [w - i - k(trRadius), i + k(trRadius)]
            : [w - i - straightCorner / 2, i + straightCorner / 2];
        const bl = blRadius
            ? [i + k(blRadius), h - i - k(blRadius)]
            : [i + straightCorner / 2, h - i - straightCorner / 2];
        const trStop = trRadius || straightCorner;
        const blStop = blRadius || straightCorner;
        const trSeg = (sweep) => (trRadius
            ? ` A${trRadius} ${trRadius} 0 0 ${sweep} ${tr[0]} ${tr[1]}`
            : (straightCorner ? ` L${tr[0]} ${tr[1]}` : ''));
        const blSeg = (sweep) => (blRadius
            ? ` A${blRadius} ${blRadius} 0 0 ${sweep} ${bl[0]} ${bl[1]}`
            : (straightCorner ? ` L${bl[0]} ${bl[1]}` : ''));

        const d = [
            `M${i} ${i} H${w - i - trStop}${trSeg(1)}`,
            `M${i} ${i} V${h - i - blStop}${blSeg(0)}`,
            `M${w - i} ${h - i} V${i + trStop}${trSeg(0)}`,
            `M${w - i} ${h - i} H${i + blStop}${blSeg(1)}`,
        ];

        svg.querySelectorAll('path').forEach((p, n) => {
            p.setAttribute('d', d[n % 4]);
        });
    };

    updatePaths();

    if (window.ResizeObserver) {
        const ro = new ResizeObserver(() => {
            updatePaths();
        });
        ro.observe(content);
    }
};

export const initTeaserListItemPixelate = () => {
    const items = document.querySelectorAll('.f-teaser-list-item');
    items.forEach((item) => {
        setupItemPixelate(item);
        setupItemFrame(item);
    });
};
